import { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { Headphones, Loader2, Phone, Mic, MicOff, Laptop, PhoneOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useStartCopilotCall,
  useCopilotVoiceToken,
  type StartCopilotCallResponse,
  type CopilotTransport,
} from '../api/copilot'
import { ApiError } from '../api/client'
import { useCopilotStore } from '../stores/copilotStore'
import { useTelemetryStore } from '../stores/telemetryStore'
import { useContact, useContactByPhone } from '../api/contacts'
import { useCopilotWebrtc } from '../components/call-staging/useCopilotWebrtc'
import { CallStagingBriefing } from '../components/contact/CallStagingBriefing'
import { CopilotSessionView } from '../components/copilot/CopilotSessionView'

// PSTN bridge flow: Twilio rings the rep's own phone first, then bridges the
// prospect. WebRTC flow: the rep takes the leg in this browser tab (the rep's
// audio transport only — post-call extraction is identical). Either way the
// page ends in the read-only reconstruction once the call wraps.
//   pstn:   start → "calling your phone" → ended
//   webrtc: start → mic → connecting → connected → ended
type CallState =
  | 'idle'
  | 'starting'
  | 'bridging'
  | 'connected'
  | 'ended'
  | 'error'

export function CopilotCallPage() {
  const { t } = useTranslation('calls')
  const { phoneNumber: rawParam } = useParams()
  const [searchParams] = useSearchParams()
  const contactId = searchParams.get('contactId')
  const phoneNumber = rawParam ? decodeURIComponent(rawParam) : ''
  // Transport is chosen on the staging page and carried here as ?mode=.
  // Default to PSTN (the no-config-required, phone-only path).
  const mode: CopilotTransport =
    searchParams.get('mode') === 'webrtc' ? 'webrtc' : 'pstn'

  const explicitContactId = contactId ? Number(contactId) : undefined
  const { data: phoneContact } = useContactByPhone(
    explicitContactId || !phoneNumber ? undefined : phoneNumber,
  )
  const briefingContactId = explicitContactId ?? phoneContact?.id
  const { data: briefingContact } = useContact(briefingContactId)
  const contactLabel = briefingContact?.contact?.name || phoneContact?.name || phoneNumber

  const startCall = useStartCopilotCall()
  const voiceToken = useCopilotVoiceToken()
  const resetCopilot = useCopilotStore((s) => s.reset)
  const webrtc = useCopilotWebrtc()

  const [state, setState] = useState<CallState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [callSid, setCallSid] = useState<string | null>(null)

  // The rep leg's terminal status callback (/copilot/call-status) emits
  // call.ended; watch the telemetry store for THIS sid so the UI swaps in the
  // post-call reconstruction the moment the call wraps, however it ended.
  // For WebRTC we also tear the browser Device down so the mic is released.
  useEffect(() => {
    if (!callSid) return
    const unsubscribe = useTelemetryStore.subscribe((s, prev) => {
      if (s.recentEvents === prev.recentEvents) return
      const ended = s.recentEvents.find(
        (e) => e.call_sid === callSid && e.event_type === 'call.ended',
      )
      if (ended) {
        if (mode === 'webrtc') webrtc.cleanup()
        setState((cur) => (cur === 'ended' ? cur : 'ended'))
      }
    })
    return unsubscribe
  }, [callSid, mode, webrtc])

  // Reflect the browser leg's connection state into the page state machine so
  // the status indicator + controls switch from "connecting" to "connected".
  // The Device now registers BEFORE the outbound POST, so the leg can be
  // auto-accepted while the page is still 'starting' (accept racing the POST
  // response) — flip from either pre-terminal state so a fast connect isn't
  // stranded on 'starting'.
  useEffect(() => {
    if (mode !== 'webrtc') return
    if (webrtc.isConnected) {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState((cur) => (cur === 'starting' || cur === 'bridging' ? 'connected' : cur))
    }
    if (webrtc.error) {
      setError(webrtc.error)
      setState((cur) => (cur === 'ended' ? cur : 'error'))
    }
  }, [mode, webrtc.isConnected, webrtc.error])

  const handleStart = useCallback(async () => {
    if (!phoneNumber) return
    setState('starting')
    setError(null)

    // WebRTC: the browser Device must be REGISTERED before the outbound leg is
    // placed — the server dials ``client:copilot-{user_id}`` the instant the
    // POST runs, and Twilio can only route it if a Device is already listening
    // under that identity (otherwise: no-answer, 0s, prospect never bridged).
    // So we mic → fetch token → register → and only THEN place the call. Each
    // step that can fail (mic denial, registration failure) stops us BEFORE the
    // POST, so the prospect is never dialed into a dead rep leg.
    if (mode === 'webrtc') {
      const micOk = await webrtc.prepare()
      if (!micOk) {
        // webrtc.prepare already set its error; surface it on the page.
        setError(webrtc.error ?? 'Microphone access denied')
        setState('error')
        return
      }
      try {
        const { token } = await voiceToken.mutateAsync()
        const registered = await webrtc.attach({ token })
        if (!registered) {
          setError(webrtc.error ?? 'Failed to connect in browser')
          setState('error')
          return
        }
      } catch (err) {
        webrtc.cleanup()
        setError(err instanceof Error ? err.message : 'Failed to connect in browser')
        setState('error')
        return
      }
    }

    try {
      const data: StartCopilotCallResponse = await startCall.mutateAsync({
        phone: phoneNumber,
        mode,
      })
      setCallSid(data.call_sid)
      // WebRTC: the Device is already registered and waiting; Twilio pushes the
      // leg as an incoming call, which the hook auto-accepts + clears the
      // press-1 gate. The auto-accept can beat this response, so don't downgrade
      // an already-'connected' page back to 'bridging'.
      setState((cur) => (cur === 'connected' ? cur : 'bridging'))
    } catch (err) {
      if (mode === 'webrtc') webrtc.cleanup()
      // The "no rep phone configured" gate comes back as HTTP 400 with a
      // FastAPI ``{detail: "..."}`` body — surface that message inline.
      let message = 'Failed to start call'
      if (err instanceof ApiError && typeof err.detail === 'string') {
        message = err.detail
      } else if (err instanceof Error) {
        message = err.message
      }
      setError(message)
      setState('error')
    }
  }, [phoneNumber, mode, startCall, voiceToken, webrtc])

  const handleReset = useCallback(() => {
    if (callSid) resetCopilot(callSid)
    if (mode === 'webrtc') webrtc.cleanup()
    setCallSid(null)
    setState('idle')
    setError(null)
  }, [callSid, mode, resetCopilot, webrtc])

  if (!phoneNumber) {
    return (
      <div className="mx-auto max-w-md space-y-4 rounded-lg border border-border bg-surface-card p-6 text-center">
        <Headphones className="mx-auto h-8 w-8 text-primary" />
        <p className="text-sm text-text-secondary">
          {t('staging.copilotEmptyStateMessage')}
        </p>
        <Link
          to="/app/calls/stage"
          className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Phone className="h-4 w-4" />
          {t('staging.copilotEmptyStateAction')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-text-primary">
          <Headphones className="h-5 w-5" />
          Co-pilot · {phoneNumber}
        </h1>
        <div className="flex items-center gap-2">
          {state === 'idle' && (
            <button
              type="button"
              onClick={handleStart}
              className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              <Phone className="h-4 w-4" /> Call with co-pilot
            </button>
          )}
          {state === 'starting' && (
            <span className="inline-flex items-center gap-2 text-sm text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              {webrtc.state === 'requesting-mic'
                ? t('copilotWebrtc.requestingMic')
                : t('copilotWebrtc.starting')}
            </span>
          )}
          {/* WebRTC in-call controls: mute + hang up live in the browser. */}
          {mode === 'webrtc' &&
            (state === 'bridging' || state === 'connected') && (
              <>
                <button
                  type="button"
                  onClick={webrtc.toggleMute}
                  className="inline-flex items-center gap-2 rounded border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-elevated"
                >
                  {webrtc.isMuted ? (
                    <MicOff className="h-4 w-4" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                  {webrtc.isMuted
                    ? t('copilotWebrtc.unmute')
                    : t('copilotWebrtc.mute')}
                </button>
                <button
                  type="button"
                  onClick={webrtc.hangUp}
                  className="inline-flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  <PhoneOff className="h-4 w-4" />
                  {t('copilotWebrtc.hangUp')}
                </button>
              </>
            )}
          {(state === 'ended' || state === 'error') && (
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-2 rounded border border-border bg-surface-card px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-elevated"
            >
              Start a new call
            </button>
          )}
        </div>
      </header>

      {briefingContactId != null && (
        <CallStagingBriefing contactId={briefingContactId} />
      )}

      {error && (
        <div className="rounded border border-red-500/40 bg-red-500/5 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* PSTN: ring-the-rep-phone status. */}
      {mode === 'pstn' && state === 'bridging' && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-card px-4 py-4 text-sm text-text-secondary">
          <Phone className="h-5 w-5 shrink-0 text-primary" />
          <span>
            Calling your phone… answer it, then we'll bridge {contactLabel}.
          </span>
        </div>
      )}

      {/* WebRTC: in-browser connection status. "bridging" = Device registering
          / waiting to accept; "connected" = the rep leg is live and the
          prospect is being dialed. */}
      {mode === 'webrtc' && state === 'bridging' && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-card px-4 py-4 text-sm text-text-secondary">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />
          <span>{t('copilotWebrtc.connecting')}</span>
        </div>
      )}
      {mode === 'webrtc' && state === 'connected' && (
        <div className="flex items-center gap-3 rounded-lg border border-success/40 bg-success/5 px-4 py-4 text-sm text-text-secondary">
          <Laptop className="h-5 w-5 shrink-0 text-success" />
          <span>
            {t('copilotWebrtc.connected', { contact: contactLabel })}
          </span>
        </div>
      )}

      {/* After the call wraps (any reason — prospect hangup, no-answer, normal
          completion), swap in the read-only reconstruction. CopilotSessionView
          hydrates from the call's persisted events + recording. */}
      {state === 'ended' && callSid && (
        <CopilotSessionView callSid={callSid} />
      )}
    </div>
  )
}
